import type { AdminUserDto, MergeHouseholdsResultDto } from '@room-mates/shared';
import { type FormEvent, type ReactElement, useState } from 'react';
import { useTranslation } from '../../shared/i18n/useLanguage.js';
import { Alert, Button, Dialog, ErrorAlert, Field, Select } from '../../shared/ui/index.js';
import { useMergeHouseholds } from './adminHooks.js';

export interface ShareHouseholdDialogProps {
  readonly user: AdminUserDto;
  readonly allUsers: readonly AdminUserDto[];
  readonly onClose: () => void;
}

/**
 * Puts two accounts into one household, so each sees the other's houses, guests
 * and sojourns as their own.
 *
 * The merge is one-way and permanent: this account's household is poured into
 * the chosen one and ceases to exist. Saying so plainly beats letting an
 * administrator discover it afterwards.
 */
export function ShareHouseholdDialog({
  user,
  allUsers,
  onClose,
}: ShareHouseholdDialogProps): ReactElement {
  const t = useTranslation();
  const merge = useMergeHouseholds();
  const [targetUserId, setTargetUserId] = useState('');
  const [result, setResult] = useState<MergeHouseholdsResultDto | null>(null);

  // Only accounts in a different household are worth offering; merging into
  // your own is refused by the server anyway.
  const candidates = allUsers.filter((candidate) => candidate.householdId !== user.householdId);
  const target = candidates.find((candidate) => candidate.id === targetUserId);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (target === undefined) {
      return;
    }
    merge.mutate(
      { householdId: user.householdId, input: { intoHouseholdId: target.householdId } },
      { onSuccess: setResult },
    );
  };

  if (result !== null) {
    return (
      <Dialog
        title={t.shareHouseholdDialog.sharedTitle}
        onClose={onClose}
        footer={<Button onClick={onClose}>{t.shareHouseholdDialog.doneButton}</Button>}
      >
        <div className="stack">
          <Alert tone="success">
            {t.shareHouseholdDialog.sharedMessage(user.username, result.householdName)}
          </Alert>
          {result.renamed.length > 0 ? (
            <div className="stack stack--tight">
              <strong>{t.shareHouseholdDialog.renamedToAvoidClash}</strong>
              <ul className="data-list">
                {result.renamed.map((rename) => (
                  <li key={`${rename.kind}-${rename.from}`} className="list-row">
                    <span className="list-row__main">
                      <span className="list-row__title">
                        {rename.from} → {rename.to}
                      </span>
                      <span className="list-row__meta">{rename.kind}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="field__hint">{t.shareHouseholdDialog.nothingRenamed}</p>
          )}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog title={t.shareHouseholdDialog.titleFor(user.username)} onClose={onClose}>
      <form className="stack" onSubmit={submit} noValidate>
        <ErrorAlert error={merge.error} />

        <Alert tone="warning">{t.shareHouseholdDialog.warning(user.username)}</Alert>

        <Field
          label={t.shareHouseholdDialog.shareWithLabel}
          hint={t.shareHouseholdDialog.shareWithHint}
        >
          {(props) => (
            <Select
              {...props}
              value={targetUserId}
              onChange={(event) => setTargetUserId(event.target.value)}
              required
            >
              <option value="">{t.shareHouseholdDialog.chooseAnAccount}</option>
              {candidates.map((candidate) => (
                <option key={candidate.id} value={candidate.id}>
                  {candidate.username} — {candidate.householdName}
                </option>
              ))}
            </Select>
          )}
        </Field>

        <div className="row row--end">
          <Button variant="secondary" onClick={onClose}>
            {t.common.cancel}
          </Button>
          <Button type="submit" loading={merge.isPending} disabled={target === undefined}>
            {t.shareHouseholdDialog.share}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
